/**
 * DJ1926Page — Pantalla de carga y descarga de la Declaración Jurada N°1926.
 *
 * Permite al contador:
 * 1. Configurar el header del año (folio RLI, resultado financiero manual,
 *    deducción 14E).
 * 2. Ingresar ajustes a la Renta Líquida Imponible (agregados/deducciones).
 * 3. Ingresar ajustes al Patrimonio Financiero (IFRS + ejercicio).
 * 4. Ver el resumen calculado en tiempo real.
 * 5. Descargar el CSV cargable al SII.
 */
import { useMemo, useState } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { Download, Plus, Trash2, Save, AlertCircle, CheckCircle2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

import apiClient from '@/lib/apiClient'
import DownloadButton from '@/components/DownloadButton'
import { useAccounts } from '../hooks/useAccounting'
import {
  useDJ1926Summary,
  useSaveDJ1926Header,
  useRLIAdjustments,
  useCreateRLIAdjustment,
  useDeleteRLIAdjustment,
  usePatrimonyAdjustments,
  useCreatePatrimonyAdjustment,
  useDeletePatrimonyAdjustment,
  type AjusteTipo,
} from '../hooks/useDJ1926'

function fmtCLP(value: string | number) {
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return '—'
  return n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
}

export function DJ1926Page() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear - 1)

  const { data: summary, isLoading: loadingSummary } = useDJ1926Summary(year)
  const { data: rliData } = useRLIAdjustments(year)
  const { data: patrimonyData } = usePatrimonyAdjustments(year)
  const { data: accounts } = useAccounts({ page_size: 9999 } as any)

  // Header form
  const [folioInicio, setFolioInicio] = useState('')
  const [folioFinal, setFolioFinal] = useState('')
  const [resultadoManual, setResultadoManual] = useState('')
  const [deduccion14e, setDeduccion14e] = useState('0')
  const saveHeader = useSaveDJ1926Header(year)

  // Cuando el summary cambia, sincronizamos el form.
  useMemo(() => {
    if (summary?.has_header) {
      setFolioInicio((summary as any).folio_rli_inicio || '')
      setFolioFinal((summary as any).folio_rli_final || '')
      setResultadoManual(summary.resultado_financiero || '')
      setDeduccion14e(summary.deduccion_14e || '0')
    }
  }, [summary])

  const years = useMemo(
    () => Array.from({ length: 8 }, (_, i) => currentYear - i),
    [currentYear],
  )

  const onSaveHeader = () => {
    saveHeader.mutate({
      folio_rli_inicio: folioInicio,
      folio_rli_final: folioFinal,
      resultado_financiero: resultadoManual || null,
      deduccion_14e: deduccion14e || '0',
    })
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      {/* Título/subtítulo los provee AccountingPage (PageHeader). */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-3">
          <label htmlFor="dj1926page-ano-comercial" className="text-sm text-content-muted">Año comercial</label>
          <select id="dj1926page-ano-comercial"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <DownloadButton path={`/accounting/dj-1926/${year}/export/`} filename={`DJ1926_${year}.csv`} />
        </div>
      </div>

      {/* Resumen calculado */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard label="Resultado Financiero" value={fmtCLP(summary.resultado_financiero)} hint="C27" />
          <StatCard label="Total Agregados" value={fmtCLP(summary.total_agregados)} hint="C16" />
          <StatCard label="Total Deducciones" value={fmtCLP(summary.total_deducciones)} hint="C17" />
          <StatCard label="Deducción 14E" value={fmtCLP(summary.deduccion_14e)} hint="C25" />
          <StatCard label="RLI / Pérdida Tributaria" value={fmtCLP(summary.rli)} hint="C28" highlight />
        </div>
      )}

      {/* Header form */}
      <section className="bg-glass backdrop-blur-glass border border-divider rounded-card p-6 shadow-card">
        <h2 className="text-sm font-bold text-content mb-4">Sección A — Identificación y configuración del año</h2>
        {!summary?.has_header && !loadingSummary && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-warning-surface border border-warning-border rounded-lg text-warning-content-strong text-sm">
            <AlertCircle size={16} /> No hay cabecera configurada para {year}. Completa el formulario y guarda antes de cargar ajustes.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Folio RLI Inicio (C23)" value={folioInicio} onChange={setFolioInicio} />
          <Field label="Folio RLI Final (C24)" value={folioFinal} onChange={setFolioFinal} />
          <Field
            label="Resultado Financiero (C27)"
            value={resultadoManual}
            onChange={setResultadoManual}
            placeholder="Auto del EERR si está vacío"
            hint="Vacío → se calcula automático del EERR del año."
          />
          <Field
            label="Deducción Art. 14 E (C25)"
            value={deduccion14e}
            onChange={setDeduccion14e}
            placeholder="0"
          />
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onSaveHeader}
            disabled={saveHeader.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg disabled:opacity-50"
          >
            <Save size={14} /> {saveHeader.isPending ? 'Guardando...' : 'Guardar cabecera'}
          </button>
        </div>
        {saveHeader.isSuccess && (
          <div className="mt-2 flex items-center gap-1 text-xs text-success-content-soft">
            <CheckCircle2 size={12} /> Cabecera guardada.
          </div>
        )}
      </section>

      {/* Sección B - Ajustes RLI */}
      <RLISection
        year={year}
        items={rliData?.items || []}
      />

      {/* Sección C - Ajustes Patrimonio */}
      <PatrimonySection
        year={year}
        items={patrimonyData?.items || []}
        accounts={(accounts as any)?.accounts || []}
      />
    </div>
  )
}

// ─── Subcomponentes ──────────────────────────────────────────────────────────

function StatCard({ label, value, hint, highlight }: { label: string; value: string; hint?: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl p-4 border ${highlight ? 'border-primary bg-primary-50 dark:bg-primary-900/20' : 'border-divider bg-surface'}`}>
      <p className="text-xs text-content-subtle uppercase">{label}</p>
      <p className={`text-lg font-bold mt-1 tabular-nums ${highlight ? 'text-primary' : 'text-content'}`}>
        {value}
      </p>
      {hint && <p className="text-xs text-content-subtle mt-0.5">{hint}</p>}
    </div>
  )
}

function Field({ label, value, onChange, placeholder, hint }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-content-muted mb-1">{label}</label>
      <input aria-label={label}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      {hint && <p className="text-xs text-content-subtle mt-0.5">{hint}</p>}
    </div>
  )
}

function RLISection({ year, items }: { year: number; items: any[] }) {
  const [showForm, setShowForm] = useState(false)
  const [partida, setPartida] = useState('')
  const [siiCode, setSiiCode] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [monto, setMonto] = useState('')
  const [tipo, setTipo] = useState<AjusteTipo>('agregado')
  const create = useCreateRLIAdjustment(year)
  const del = useDeleteRLIAdjustment(year)

  const onAdd = async () => {
    if (!partida || !monto) return
    await create.mutateAsync({
      partida_concept: partida,
      sii_account_code: siiCode,
      descripcion,
      monto,
      tipo,
    })
    setPartida(''); setSiiCode(''); setDescripcion(''); setMonto('')
    setShowForm(false)
  }

  return (
    <section className="bg-glass backdrop-blur-glass border border-divider rounded-card p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-bold text-content">Sección B — Ajustes a la Renta Líquida Imponible</h2>
          <p className="text-xs text-content-subtle">Agregados y deducciones tributarias al Resultado Financiero.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface-muted text-content rounded-lg border border-divider hover:bg-surface-hover"
        >
          <Plus size={12} /> Agregar ajuste
        </button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 border border-divider-strong rounded-lg bg-glass-3/30">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <label htmlFor="dj1926page-concepto-partida-c2" className="block text-xs text-content-muted mb-1">Concepto / Partida (C2)</label>
              <input id="dj1926page-concepto-partida-c2" value={partida} onChange={e => setPartida(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
            <div>
              <label htmlFor="dj1926page-cuenta-sii-c3" className="block text-xs text-content-muted mb-1">Cuenta SII (C3)</label>
              <input id="dj1926page-cuenta-sii-c3" value={siiCode} onChange={e => setSiiCode(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="dj1926page-descripcion-c4" className="block text-xs text-content-muted mb-1">Descripción (C4)</label>
              <input id="dj1926page-descripcion-c4" value={descripcion} onChange={e => setDescripcion(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
            <div>
              <label htmlFor="dj1926page-tipo-c6" className="block text-xs text-content-muted mb-1">Tipo (C6)</label>
              <select id="dj1926page-tipo-c6" value={tipo} onChange={e => setTipo(e.target.value as AjusteTipo)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface">
                <option value="agregado">Agregado</option>
                <option value="deduccion">Deducción</option>
              </select>
            </div>
            <div className="md:col-span-1">
              <label htmlFor="dj1926page-monto-c5" className="block text-xs text-content-muted mb-1">Monto (C5)</label>
              <input id="dj1926page-monto-c5" type="number" value={monto} onChange={e => setMonto(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-xs text-content-muted">Cancelar</button>
            <button onClick={onAdd} disabled={create.isPending || !partida || !monto} className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg disabled:opacity-50">
              {create.isPending ? 'Guardando…' : 'Agregar'}
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-content-subtle py-8 text-center">Sin ajustes RLI registrados para este año.</p>
      ) : (
        <DataTable
          columns={[
            { key: 'line_number', header: 'N°' },
            { key: 'partida_concept', header: 'Concepto' },
            { key: 'sii_account_code', header: 'Cuenta SII', render: it => it.sii_account_code || '—' },
            { key: 'descripcion', header: 'Descripción' },
            { key: 'monto', header: 'Monto', numeric: true, sortValue: it => Number(it.monto), render: it => fmtCLP(it.monto) },
            {
              key: 'tipo', header: 'Tipo',
              render: it => (
                <span className={`px-2 py-0.5 text-xs rounded ${it.tipo === 'agregado' ? 'bg-warning-surface text-warning-content' : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                  {it.tipo === 'agregado' ? 'Agregado' : 'Deducción'}
                </span>
              ),
            },
            {
              key: 'acciones', header: '',
              render: it => (
                <div className="text-right">
                  <button onClick={() => del.mutate(it.id)} className="p-1 text-danger-solid hover:bg-danger-surface rounded">
                    <Trash2 size={12} />
                  </button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={it => it.id}
          surface={false}
          minWidth={860}
          caption="Ajustes RLI de la DJ 1926"
        />
      )}
    </section>
  )
}

function PatrimonySection({ year, items, accounts }: { year: number; items: any[]; accounts: any[] }) {
  const [showForm, setShowForm] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [partidaCode, setPartidaCode] = useState('')
  const [ifrsD, setIfrsD] = useState('')
  const [ifrsA, setIfrsA] = useState('')
  const [ejerD, setEjerD] = useState('')
  const [ejerA, setEjerA] = useState('')
  const create = useCreatePatrimonyAdjustment(year)
  const del = useDeletePatrimonyAdjustment(year)

  const onAdd = async () => {
    if (!accountId) return
    await create.mutateAsync({
      account_id: accountId,
      partida_code: partidaCode,
      ifrs_deudor: ifrsD || '0',
      ifrs_acreedor: ifrsA || '0',
      ejercicio_deudor: ejerD || '0',
      ejercicio_acreedor: ejerA || '0',
    })
    setAccountId(''); setPartidaCode(''); setIfrsD(''); setIfrsA(''); setEjerD(''); setEjerA('')
    setShowForm(false)
  }

  return (
    <section className="bg-glass backdrop-blur-glass border border-divider rounded-card p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-bold text-content">Sección C — Ajustes al Patrimonio Financiero</h2>
          <p className="text-xs text-content-subtle">Ajustes IFRS 1era aplicación + ajustes del ejercicio que afectan patrimonio.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface-muted text-content rounded-lg border border-divider hover:bg-surface-hover"
        >
          <Plus size={12} /> Agregar ajuste
        </button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 border border-divider-strong rounded-lg bg-glass-3/30">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <label htmlFor="dj1926page-cuenta-del-plan-de-cuentas" className="block text-xs text-content-muted mb-1">Cuenta del Plan de Cuentas</label>
              <EntityAutocomplete id="dj1926page-cuenta-del-plan-de-cuentas"
                options={accounts.map((a: any) => ({ id: a.id, label: `${a.code} — ${a.name}` }))}
                value={accountId}
                onChange={setAccountId}
                placeholder="Seleccionar…"
                allowClear
              />
            </div>
            <div>
              <label htmlFor="dj1926page-cod-partida-c8" className="block text-xs text-content-muted mb-1">Cod. Partida (C8)</label>
              <input id="dj1926page-cod-partida-c8" value={partidaCode} onChange={e => setPartidaCode(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
            <div>
              <label htmlFor="dj1926page-ifrs-deudor-c11" className="block text-xs text-content-muted mb-1">IFRS Deudor (C11)</label>
              <input id="dj1926page-ifrs-deudor-c11" type="number" value={ifrsD} onChange={e => setIfrsD(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
            <div>
              <label htmlFor="dj1926page-ifrs-acreedor-c12" className="block text-xs text-content-muted mb-1">IFRS Acreedor (C12)</label>
              <input id="dj1926page-ifrs-acreedor-c12" type="number" value={ifrsA} onChange={e => setIfrsA(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
            <div>
              <label htmlFor="dj1926page-ejercicio-deudor-c13" className="block text-xs text-content-muted mb-1">Ejercicio Deudor (C13)</label>
              <input id="dj1926page-ejercicio-deudor-c13" type="number" value={ejerD} onChange={e => setEjerD(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
            <div>
              <label htmlFor="dj1926page-ejercicio-acreedor-c14" className="block text-xs text-content-muted mb-1">Ejercicio Acreedor (C14)</label>
              <input id="dj1926page-ejercicio-acreedor-c14" type="number" value={ejerA} onChange={e => setEjerA(e.target.value)} className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface" />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-xs text-content-muted">Cancelar</button>
            <button onClick={onAdd} disabled={create.isPending || !accountId} className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg disabled:opacity-50">
              {create.isPending ? 'Guardando…' : 'Agregar'}
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-content-subtle py-8 text-center">Sin ajustes de patrimonio registrados para este año.</p>
      ) : (
        <DataTable
          columns={[
            { key: 'line_number', header: 'N°' },
            {
              key: 'account_code', header: 'Cuenta',
              render: it => <><span className="font-mono text-xs">{it.account_code}</span> — {it.account_name}</>,
            },
            { key: 'sii_code', header: 'SII', render: it => <span className="text-xs">{it.sii_code || '—'}</span> },
            { key: 'ifrs_deudor', header: 'IFRS Deu.', numeric: true, render: it => fmtCLP(it.ifrs_deudor) },
            { key: 'ifrs_acreedor', header: 'IFRS Acr.', numeric: true, render: it => fmtCLP(it.ifrs_acreedor) },
            { key: 'ejercicio_deudor', header: 'Ejer. Deu.', numeric: true, render: it => fmtCLP(it.ejercicio_deudor) },
            { key: 'ejercicio_acreedor', header: 'Ejer. Acr.', numeric: true, render: it => fmtCLP(it.ejercicio_acreedor) },
            {
              key: 'acciones', header: '',
              render: it => (
                <div className="text-right">
                  <button onClick={() => del.mutate(it.id)} className="p-1 text-danger-solid hover:bg-danger-surface rounded">
                    <Trash2 size={12} />
                  </button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={it => it.id}
          surface={false}
          minWidth={900}
          caption="Ajustes de patrimonio de la DJ 1926"
        />
      )}
    </section>
  )
}

export default DJ1926Page
